export enum EmployerPermissions {
  subscription = "subscription",
  teammates = "teammates",
  organizations = "organizations",
  jobs = "jobs",
  verification_checks = "verification_checks",
}

type ProjectAsset = {
  name: string;
  mimetype: string;
  path: string;
  alt: string;
  url: string;
};

export type Project = {
  name: string;
  start_date: string;
  end_date: string;
  currently_working: boolean;
  location: string;
  skills: string[];
  description: string;
  assets: ProjectAsset[];
};

// ---------------------------------------------------------------------------
// Certificate field_rules — dynamic per-certificate form configuration
// ---------------------------------------------------------------------------

export type FieldRequirement = "required" | "optional" | "hidden";

export type FieldRule = {
  requirement: FieldRequirement;
  label?: string;
  help?: string;
  allow_unknown?: boolean;
  default_status?: "no_expiry" | "has_expiry";
};

export type CertificateFieldRules = {
  certificate_number?: FieldRule;
  issue_date?: FieldRule;
  expiry?: FieldRule;
  trainer_name?: FieldRule;
  document?: FieldRule;
};

export type ExpiryStatus = "has_expiry" | "no_expiry" | "unknown";

/** Shape of a single certificate inside the master data response. */
export type MasterCertificate = {
  id: string;
  certificate_name: string;
  field_rules?: CertificateFieldRules;
};

/** Shape of an authority in the master data response. */
export type MasterAuthority = {
  id: string;
  authority_name: string;
  default_field_rules?: CertificateFieldRules;
  certificates: MasterCertificate[];
};

export type Certificate = {
  jobseeker_certificate_id: string;
  certificate_id: string;
  certificate_number: string;
  // Optional: the quickstart omits it for file-less certs, and the API
  // returns certs with no stored document.
  certificate_url?: string;
  issue_date: string;
  certificate_expiry: string;
  issuer_name: string;
  certificate_name?: string;
  name?: string; // For backward compatibility
  /** Backend review state (jobseeker_certificate model); defaults to
   * "pending" on create. Rendered as a badge by CertificateCard. */
  verification_status?: "pending" | "verified" | "rejected" | "expired";
  verification_notes?: string;
  expiry_status?: ExpiryStatus;
  issue_date_unknown?: boolean;
  trainer_name?: string;
};

export type Experience = {
  title?: string;
  job_title?: string;
  company_name: string;
  start_date: { month: number; year: number };
  end_date?: { month: number; year: number };
  location: string;
  description?: string;
};

export type Education = {
  school?: string;
  institution?: string;
  degree: string;
  start_date: { month: number; year: number };
  end_date?: { month: number; year: number };
  grade?: string;
  description?: string;
};

export type Language = {
  language: string;
  proficiency: string;
};

type PortfolioItem = {
  title: string;
  description?: string;
  url?: string;
};

export type JobseekerAddress = {
  country: string;
  state: string;
  city: string;
  zip_code: string;
};

/**
 * How far a Job Seeker will travel for work. `can_travel === null` means they
 * have never been asked — render that as "travel range not set", never as a
 * default value, because employers act on what this says.
 *
 * `travel_radius_miles` is never 0; "close to home" stores CLOSE_TO_HOME_MILES.
 * See `@/utils/travel`.
 */
export type TravelFields = {
  can_travel?: boolean | null;
  travel_radius_miles?: number | null;
  has_vehicle?: boolean | null;
};

export type Jobseeker = {
  bc_verified: boolean;
  id: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  deleted: boolean;
  type: string;
  photo: string;
  photo_thumbnail_url: string;
  job_title: string;
  work_preference: string;
  match_percentage: number;
  address?: JobseekerAddress;
  geo?: { lng: number; lat: number };
  education: Education[];
  experience: Experience[];
  work_experience?: Experience[];
  certificates: Certificate[];
  skills: string[];
  website: string;
  about_me: string;
  bio?: string;
  career_goal?: string;
  languages?: Language[];
  portfolio_items?: PortfolioItem[];
  recruit_status?: string;
  onboarding_step?: number;
  visit_count?: number;
  experience_years: number;
  projects: Project[];
  video: { url: string; name: string; size_mb: number };
  resume: { url: string; path: string; name: string; size_mb: number };
  is_favourited?: boolean;
  updated_at: string;

  /**
   * Distance from the search origin (the job site, or the employer's chosen
   * point), returned per candidate by `POST /jobseekers`. Computed server-side
   * so the client can sort and tier without doing geo math.
   */
  distance_miles?: number | null;
  /** null when the candidate has not stated a range. */
  within_stated_range?: boolean | null;
} & TravelFields;

/** `POST /jobseekers`: one page of candidates plus the page arithmetic. */
export type JobseekerSearchResult = {
  total: number;
  jobseekers: Jobseeker[];
  pagination?: { page: number; per_page: number; total_pages: number; total: number };
};

export type UpdateUserPayload = {
  // Identity
  first_name?: string;
  last_name?: string;
  phone?: string;
  about_me?: string;

  // Professional
  job_title?: string;
  experience_years?: number;
  work_preference?: string;
  website?: string;
  timezone?: string;
  skills?: string[];
  languages?: Language[];

  // Location
  address?: Partial<JobseekerAddress>;
  full_address?: string;
  geo?: { lng?: number; lat?: number };

  // Collections — typed loosely because user.ts and resume.ts define
  // conflicting sub-types (e.g. Project.start_date is string vs Date).
  // Tighten once the two type families are unified.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  education?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  experience?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projects?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  certificates?: any[];
} & TravelFields;

/** Shape of the data parameter for the POST /users/:id/certificate endpoint. */
export type CreateCertificatePayload = {
  certificate_id: string;
  /** Master authority id required by the backend alongside certificate_id. */
  item_key: string;
  certificate_number?: string | null;
  issue_date?: string | null;
  certificate_expiry?: string | null;
  certificate_url?: string | null;
  issuer_name: string;
  certificate_name: string;
  expiry_status?: ExpiryStatus;
  issue_date_unknown?: boolean;
  trainer_name?: string | null;
};

export type OnboardingStats = {
  type: string;
  email_verified: boolean;
  onboarding_step: number;
  organization_owner: boolean;
  organization: { status: string };
  password_changed: boolean;
  popups?: {
    resume?: boolean;
  };
};
